import { useState, useEffect } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from '../../shared/adminApi';

export interface Campaign {
  campaign_id: string;
  campaign_name: string;
  scan_count: number;
  created_at: string;
  last_scanned_at: string;
}

interface RawQrCodeRow {
  campaign_id: string;
  campaign_name?: string;
  scan_count?: number;
  created_at?: string;
  last_scanned_at?: string;
}

/**
 * QR campaign scan counts for the stage `env` selects.
 *
 * `status=all` = active + ended, mirroring the legacy /admin/campaigns endpoint
 * this replaced. That legacy Lambda is still deployed and can be removed in a
 * follow-up once this is verified live.
 */
export function useCampaignStats(env: Env): {
  data: Campaign[];
  loading: boolean;
  error: string | null;
} {
  const { request } = useAdminApi();
  const [data, setData] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    (async () => {
      try {
        const json = await request<{ data?: RawQrCodeRow[] }>({
          env,
          path: '/admin/qr-codes',
          query: { type: 'campaign', status: 'all' },
        });
        if (!isMounted) return;
        setData(
          (json.data ?? []).map((r) => ({
            campaign_id: r.campaign_id,
            campaign_name: r.campaign_name ?? '',
            scan_count: r.scan_count ?? 0,
            created_at: r.created_at ?? '',
            last_scanned_at: r.last_scanned_at ?? '',
          })),
        );
        setError(null);
      } catch (err: unknown) {
        if (!isMounted) return;
        setData([]);
        setError(err instanceof ApiError ? err.message : 'Failed to load campaign stats');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [request, env]);

  return { data, loading, error };
}
